import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { contractFramesAPI } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Plus,
  Search,
  FileText,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Download,
  Calendar,
  Circle
} from "lucide-react";
import { cn } from "../lib/utils";

export default function ContratosMarco() {
  const navigate = useNavigate();
  const { user, isLogistica } = useAuth();
  
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const trafficLightColors = {
    rojo: { bg: "bg-red-100", text: "text-red-800", dot: "text-red-500" },
    amarillo: { bg: "bg-yellow-100", text: "text-yellow-800", dot: "text-yellow-500" },
    verde: { bg: "bg-green-100", text: "text-green-800", dot: "text-green-500" }
  };

  useEffect(() => {
    fetchFrames();
  }, [statusFilter]);

  const fetchFrames = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      
      const response = await contractFramesAPI.getAll(params);
      setFrames(response.data);
    } catch (error) {
      toast.error("Error al cargar contratos marco");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (frameId) => {
    if (!window.confirm("¿Está seguro de eliminar este contrato marco?")) return;
    
    try {
      await contractFramesAPI.delete(frameId);
      toast.success("Contrato marco eliminado");
      fetchFrames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleGeneratePDF = async (frameId) => {
    try {
      const response = await contractFramesAPI.generatePDF(frameId);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrato_${frameId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF generado y descargado");
      fetchFrames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar PDF");
    }
  };

  const filteredFrames = frames.filter(f => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      f.client_name?.toLowerCase().includes(searchLower) ||
      f.corporate_name?.toLowerCase().includes(searchLower) ||
      f.service_description?.toLowerCase().includes(searchLower) ||
      f.id?.toLowerCase().includes(searchLower)
    );
  });

  if (isLogistica) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-slate-500">No tiene permisos para acceder a esta sección.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="contratos-marco-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">
              Contratos Marco
            </h1>
            <p className="text-slate-500 mt-1">
              Contratos multi-día con itinerario y generación de PDF
            </p>
          </div>
          <Button onClick={() => navigate("/contratos-marco/nuevo")} data-testid="new-frame-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Contrato Marco
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por cliente, corporativo..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="borrador">Borrador</SelectItem>
                  <SelectItem value="emitido">Emitido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full mx-auto"></div>
                <p className="text-slate-500 mt-3">Cargando contratos...</p>
              </div>
            ) : filteredFrames.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No hay contratos marco</p>
                <Button
                  variant="link"
                  onClick={() => navigate("/contratos-marco/nuevo")}
                  className="mt-2"
                >
                  Crear el primer contrato marco
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Periodo</TableHead>
                    <TableHead>Días</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Versión</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFrames.map((frame) => (
                    <TableRow
                      key={frame.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/contratos-marco/${frame.id}`)}
                      data-testid={`frame-row-${frame.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Circle
                          className={cn(
                            "w-3 h-3 fill-current",
                            trafficLightColors[frame.traffic_light]?.dot || "text-slate-300"
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{frame.client_name}</p>
                          {frame.corporate_name && (
                            <p className="text-sm text-slate-500">{frame.corporate_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {frame.start_date} - {frame.end_date}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{frame.total_days} días</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={frame.status === "emitido" ? "default" : "secondary"}
                        >
                          {frame.status === "emitido" ? "Emitido" : "Borrador"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {frame.current_version > 0 ? (
                          <Badge variant="outline">v{frame.current_version}</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ${frame.total_price?.toLocaleString("es-MX") || "0"}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`menu-${frame.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/contratos-marco/${frame.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver Detalles
                            </DropdownMenuItem>
                            {frame.status === "borrador" && (
                              <DropdownMenuItem onClick={() => navigate(`/contratos-marco/${frame.id}`)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleGeneratePDF(frame.id)}>
                              <Download className="w-4 h-4 mr-2" />
                              {frame.status === "emitido" ? "Nueva Versión PDF" : "Generar PDF"}
                            </DropdownMenuItem>
                            {frame.status === "borrador" && (
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDelete(frame.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Contratos</p>
                <p className="text-2xl font-bold">{frames.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Edit className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Borradores</p>
                <p className="text-2xl font-bold">
                  {frames.filter(f => f.status === "borrador").length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Emitidos</p>
                <p className="text-2xl font-bold">
                  {frames.filter(f => f.status === "emitido").length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
